import { useEffect, useState } from 'react';
import { useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { AdminSidebar } from './AdminSidebar';

const AdminLayout = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [collapsed, setCollapsed] = useState(false);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async () => {
      console.log('Starting admin check with user:', user);
      
      if (!user) {
        console.log('No user found, redirecting to admin login');
        navigate('/admin-web/login');
        return;
      }

      try {
        console.log('Checking admin status for user ID:', user.id);
        
        // Check admin status directly
        const { data: admin, error: adminError } = await supabase
          .from('admins')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        console.log('Admin check result:', { admin, adminError });

        if (adminError) {
          console.error('Error checking admin status:', adminError);
          throw adminError;
        }

        if (!admin) {
          console.log('User is not an admin');
          toast({
            title: "Access Denied",
            description: "You don't have admin privileges",
            variant: "destructive"
          });
          navigate('/');
          return;
        }

        console.log('Admin access granted:', admin);
        setIsCheckingAdmin(false);
      } catch (error) {
        console.error('Unexpected error checking admin status:', error);
        toast({
          title: "Error",
          description: "An unexpected error occurred while checking admin status",
          variant: "destructive"
        });
        navigate('/');
      }
    };

    checkAdminStatus();
  }, [user, navigate, toast]);

  // Add meta tags to prevent indexing
  useEffect(() => {
    const metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    metaRobots.content = 'noindex, nofollow';
    document.head.appendChild(metaRobots);

    return () => {
      document.head.removeChild(metaRobots);
    };
  }, []);

  if (!user || isCheckingAdmin) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="flex h-screen">
        <AdminSidebar collapsed={collapsed} setCollapsed={setCollapsed} />
        
        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          <div className="p-8">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;